import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { containersNeeded, round6 } from '@core/proposals/pricing';
import { SpecyCapacityGrid } from '@core/species/species-capacity.types';

/** One row of the offer form: a container type and how many of them. */
export interface OfferContainerFormLine {
  quantityTypeId: number | null;
  count: number | null;
}

export interface ContainerTypeRef {
  id: number;
  name: string;
}

/** Capacity of one container type for the species and dry type of the offer. */
export interface CapacityCellRef {
  quantityTypeId: number;
  capacityM3: number | null;
}

export interface ContainerPreviewLine {
  quantityTypeId: number;
  count: number;
  capacityM3: number | null;
  subtotalM3: number | null;
}

export interface ContainerPreview {
  perLine: ContainerPreviewLine[];
  totalM3: number;
  /** Names of the container types that have no capacity for this species and dry type. */
  missing: string[];
}

/** Container line as the backend returns it in `ProductOffer.containers`. */
export interface OfferContainerSummaryLine {
  count: number;
  quantityType?: { name: string } | null;
}


/** Container types that line `currentIndex` may pick: every type not used by ANOTHER line. */
export function availableContainerTypes(
  all: ContainerTypeRef[],
  lines: OfferContainerFormLine[],
  currentIndex: number
): ContainerTypeRef[] {
  const usedByOthers = new Set<number>();
  lines.forEach((line, index) => {
    if (index !== currentIndex && line.quantityTypeId !== null && line.quantityTypeId !== undefined) {
      usedByOthers.add(+line.quantityTypeId);
    }
  });
  return all.filter((type) => !usedByOthers.has(+type.id));
}

/**
 * Preview of the offer volume while the user edits the lines. The value saved by the
 * backend after the save is the authoritative one. Incomplete lines (no type, count
 * missing, zero, negative or not an integer) are ignored, lines whose type has no
 * capacity are reported in `missing` and do not add volume.
 */
export function previewContainerVolume(
  lines: OfferContainerFormLine[],
  gridRowCells: CapacityCellRef[],
  typeNames: Record<number, string>
): ContainerPreview {
  const perLine: ContainerPreviewLine[] = [];
  const missing: string[] = [];
  let total = 0;

  lines.forEach((line) => {
    const typeId = line.quantityTypeId === null ? NaN : +line.quantityTypeId;
    const count = line.count === null ? NaN : +line.count;
    if (!Number.isFinite(typeId) || !Number.isInteger(count) || count < 1) {
      return;
    }

    const capacity =
      gridRowCells.find((cell) => +cell.quantityTypeId === typeId)?.capacityM3 ?? null;
    if (capacity === null) {
      const name = typeNames[typeId] ?? String(typeId);
      if (!missing.includes(name)) {
        missing.push(name);
      }
      perLine.push({ quantityTypeId: typeId, count, capacityM3: null, subtotalM3: null });
      return;
    }

    const subtotal = round6(count * capacity);
    total += subtotal;
    perLine.push({ quantityTypeId: typeId, count, capacityM3: capacity, subtotalM3: subtotal });
  });

  return { perLine, totalM3: round6(total), missing };
}

/** D19 preview: how many containers of ONE type the requested volume needs. */
export interface ContainerSuggestion {
  quantityTypeId: number;
  count: number;
  capacityM3: number;
  /** count x capacity. */
  totalCapacityM3: number;
  /** total capacity - requested volume, never negative. */
  idleM3: number;
}

/**
 * Mirror of D19 for the offer form: ceil(requested / capacity) with the float-noise guard of the backend. Null when the
 * volume is not a number > 0, no type is chosen, or the grid has no capacity for the type. The backend value after the
 * save is the authoritative one.
 */
export function suggestContainerLine(
  requestedVolumeM3: number | null,
  quantityTypeId: number | null,
  gridRowCells: CapacityCellRef[]
): ContainerSuggestion | null {
  if (typeof requestedVolumeM3 !== 'number' || !Number.isFinite(requestedVolumeM3) || requestedVolumeM3 <= 0) {
    return null;
  }
  if (quantityTypeId === null || quantityTypeId === undefined || !Number.isFinite(+quantityTypeId)) {
    return null;
  }
  const capacity = gridRowCells.find((cell) => +cell.quantityTypeId === +quantityTypeId)?.capacityM3 ?? null;
  if (capacity === null || !(capacity > 0)) {
    return null;
  }
  const count = containersNeeded(requestedVolumeM3, capacity);
  const totalCapacityM3 = round6(count * capacity);
  return {
    quantityTypeId: +quantityTypeId,
    count,
    capacityM3: capacity,
    totalCapacityM3,
    idleM3: Math.max(0, round6(totalCapacityM3 - requestedVolumeM3)),
  };
}

/**
 * Billed volume, idle capacity and shortfall of the offer form: billed = requested when it is a number > 0, otherwise the
 * capacity total; idle = max(0, capacity - requested); shortfall = max(0, requested - capacity), 0 while a container type
 * has no capacity (cannot verify, D7). The backend validates on save.
 */
export function previewBilledVolume(
  requestedVolumeM3: number | null,
  preview: ContainerPreview
): { billedM3: number; idleM3: number; shortfallM3: number } {
  const usable =
    typeof requestedVolumeM3 === 'number' && Number.isFinite(requestedVolumeM3) && requestedVolumeM3 > 0;
  if (!usable) {
    return { billedM3: preview.totalM3, idleM3: 0, shortfallM3: 0 };
  }
  return {
    billedM3: round6(requestedVolumeM3),
    idleM3: Math.max(0, round6(preview.totalM3 - requestedVolumeM3)),
    shortfallM3: preview.missing.length > 0 ? 0 : Math.max(0, round6(requestedVolumeM3 - preview.totalM3)),
  };
}

/** The capacities of one dry type (a grid row) as the cells the offer form needs. */
export function capacityCellsForDryType(
  grid: SpecyCapacityGrid | null,
  dryTypeId: number
): CapacityCellRef[] {
  const row = grid?.rows.find((candidate) => +candidate.dryTypeId === +dryTypeId);
  if (!row) {
    return [];
  }
  return row.cells.map((cell) => ({
    quantityTypeId: cell.quantityTypeId,
    capacityM3: cell.capacityM3 ?? null,
  }));
}

/** 88 -> "88", 99.6 -> "99.6", 28.125 -> "28.13" (2 decimals, trailing zeros trimmed). */
export function formatVolumeM3(value: number): string {
  return String(Number(value.toFixed(2)));
}

/**
 * "2 × 20 STD + 1 × 40 HC (88 m³)"; the volume is omitted when the backend did not send it. With a requested volume
 * (D19) it reads "2 × 20 STD · 50 m³ pedidos": `requested.label` is the already translated "{{volume}} m³ pedidos".
 */
export function containersSummary(
  containers: OfferContainerSummaryLine[] | null | undefined,
  volumeM3: number | null | undefined,
  requested?: { volumeM3: number; label: string } | null
): string {
  if (!containers || containers.length === 0) {
    return '';
  }
  const parts = containers
    .map((container) => `${container.count} × ${container.quantityType?.name ?? ''}`.trim())
    .join(' + ');
  if (requested) {
    return `${parts} · ${requested.label}`;
  }
  if (volumeM3 === null || volumeM3 === undefined) {
    return parts;
  }
  return `${parts} (${formatVolumeM3(volumeM3)} m³)`;
}

/** Form group of one container line: a type and an integer count >= 1. */
export function buildContainerLineGroup(
  line: OfferContainerFormLine = { quantityTypeId: null, count: 1 }
): UntypedFormGroup {
  return new UntypedFormGroup({
    quantityTypeId: new UntypedFormControl(line.quantityTypeId, [Validators.required]),
    count: new UntypedFormControl(line.count, [
      Validators.required,
      Validators.min(1),
      Validators.pattern(/^\d+$/),
    ]),
  });
}

/** FormArray validator: a container offer needs at least one container line. */
export function atLeastOneContainer(control: AbstractControl): ValidationErrors | null {
  const length = (control as { length?: number }).length ?? 0;
  return length >= 1 ? null : { containersRequired: true };
}

/** Validator of the requested volume: blank is fine (no requested volume), otherwise a finite number > 0. */
export function positiveRequestedVolume(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = +value;
  return Number.isFinite(parsed) && parsed > 0 ? null : { requestedVolumePositive: true };
}

/** The requested volume a record carries: blank or unparsable becomes null, NEVER 0 (+'' === 0). */
export function toRequestedVolume(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = +(value as number);
  return Number.isFinite(parsed) ? parsed : null;
}
